import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Compact pagination with ellipsis. testIdPrefix drives data-testid names
 * (e.g., "rilisan" => rilisan-pagination, rilisan-page-3, rilisan-page-next).
 */
const Pagination = ({ currentPage, totalPages, onChange, testIdPrefix = 'page' }) => {
  if (totalPages <= 1) return null;

  const buildPages = () => {
    const pages = [];
    const push = (v) => pages.push(v);
    const window = 1; // neighbors on each side

    push(1);
    const start = Math.max(2, currentPage - window);
    const end = Math.min(totalPages - 1, currentPage + window);
    if (start > 2) push('…l');
    for (let i = start; i <= end; i++) push(i);
    if (end < totalPages - 1) push('…r');
    if (totalPages > 1) push(totalPages);
    return pages;
  };

  const btn =
    'min-w-9 h-9 px-3 rounded-full text-xs font-semibold btn-transition border flex items-center justify-center';
  const active =
    'bg-[#D4A94F] text-black border-[#D4A94F] shadow-[0_6px_20px_-6px_rgba(212,169,79,0.7)]';
  const idle =
    'bg-transparent text-neutral-300 border-white/10 hover:border-[#D4A94F]/50 hover:text-white';
  const disabled = 'opacity-30 cursor-not-allowed';

  return (
    <nav
      className="flex flex-wrap items-center justify-center gap-2 mt-12"
      aria-label="Pagination"
      data-testid={`${testIdPrefix}-pagination`}
    >
      <button
        type="button"
        onClick={() => onChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`${btn} ${idle} ${currentPage <= 1 ? disabled : ''}`}
        aria-label="Sebelumnya"
        data-testid={`${testIdPrefix}-page-prev`}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {buildPages().map((p, idx) =>
        typeof p === 'number' ? (
          <button
            key={`${p}-${idx}`}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === currentPage ? 'page' : undefined}
            className={`${btn} ${p === currentPage ? active : idle}`}
            data-testid={`${testIdPrefix}-page-${p}`}
          >
            {p}
          </button>
        ) : (
          <span key={`e-${idx}`} className="text-neutral-500 px-1 select-none">
            …
          </span>
        )
      )}

      <button
        type="button"
        onClick={() => onChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`${btn} ${idle} ${currentPage >= totalPages ? disabled : ''}`}
        aria-label="Berikutnya"
        data-testid={`${testIdPrefix}-page-next`}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
};

export default Pagination;
